<template>
  <module-list-layout :createAction="createAction" :module="module" class="p-2" v-if="amReady">
    <el-dialog :visible.sync="dialogVisible" title="" top="5vh" width="75%">
      <moduleForm
          :autoBind="false"
          :formHeader="true"
          :edit-mode="editMode"
          :bindingForView="false"
          :dataToBind="dataToBind"
          :on-edit-callback="afterEdit"
          :on-create-callback="afterCreate"
          :moduleDeleteAction='moduleFormDeleteAction'
          :formTitle="((editMode)? 'Update ' : 'Create ') + module.info.caption"
          v-if="dialogVisible"></moduleForm>
    </el-dialog>

    <div class="row pl-3 pr-3">
      <div class="col-12 col-md-6 pt-1">
        <dg-text-field
            :field_span="22"
            :label_span="22"
            field_mark=""
            placeholder="Search roles..."
            @amUpdated="userSearchUpdated"
            :liveUpdate="true"></dg-text-field>
      </div>
      <div class="col-12 col-md-6 pt-1">
        <pagination
            :currentPage="currentPage"
            paginateClass=""
            :page-count="getPagesCount()"
            :page-range="4"
            :margin-pages="2"
            :navigateHandler="navigateToPage"></pagination>
      </div>
    </div>

    <app-ag-grid :agGrid="agGrid"></app-ag-grid>

    <el-dialog :visible.sync="deleteDialogVisible" center title="Warning" width="420px">
      <div align="center" class="mt-2 mb-2">You want to remove this role?</div>
      <span class="dialog-footer" slot="footer">
        <el-button @click="deleteDialogVisible = false" size="small">Cancel</el-button>
        <el-button @click="removeActionApi" size="small" type="danger">Yes</el-button>
      </span>
    </el-dialog>
  </module-list-layout>
</template>

<script>
import {mapActions} from 'vuex'
import actionsCell from './ag-grid/ActionsCell'
import module from './../../index.js'
import {notify, notifySuccess} from '@/system-services/notify.js'

export default {
  components: {
    pagination: () => import('@/components/pagination/View.vue'),
    moduleForm: () => import('../../form/View'),
    moduleListLayout: () => import('@/modules-config/layouts/list/View.vue')
  },
  data() {
    return {
      deleteDialogVisible: false,
      targetParams: null,
      userSearch: '',
      userSearchTimeOut: null,
      amReady: false,
      module: {
        ...module
      },
      currentPage: 1,
      pageChunkSize: 12,
      pageCount: 1,
      editMode: false,
      dialogVisible: false,
      recordsCounter: 0,
      dataToBind: null,
      rowToEdit: null,
      agGrid: {
        pagination: false,
        enableRtl: this.$langConfig.dir == 'rtl',
        agGridStyle: 'width: 100%; height: 650px; padding: 0',
        agGridClass: 'ag-theme-material',
        gridOptions: {
          suppressCellSelection: true,
          onGridReady: function (params) {
            params.api.sizeColumnsToFit()
          }
        },
        columnDefs: [
          {
            field: 'id',
            headerName: 'ID',
            headerClass: this.$langConfig.lang + '-lang-text text-capitalize',
            width: 80
          },
          {
            field: 'name',
            headerName: 'Role Name',
            headerClass: this.$langConfig.lang + '-lang-text text-capitalize',
            cellClass: 'text-capitalize'
          },
          {
            field: 'description',
            headerName: 'Description',
            headerClass: this.$langConfig.lang + '-lang-text text-capitalize',
          },
          {
            field: 'created_at',
            headerName: 'Created At',
            headerClass: this.$langConfig.lang + '-lang-text text-capitalize',
            cellRenderer: (params) => {
              if (params.value) {
                return new Date(params.value).toLocaleDateString()
              }
              return ''
            }
          },
          {
            width: 120,
            headerName: this.$ml.get('actions'),
            headerClass: this.$langConfig.lang + '-lang-text text-capitalize',
            cellRendererFramework: $vue.extend(actionsCell),
            cellRendererParams: {
              props: {
                editAction: this.editAction,
                removeAction: this.removeAction
              }
            }
          }
        ],
        rowData: []
      }
    }
  },
  methods: {
    ...mapActions(module.info.name + '-api', {
      getEntityList: 'list',
      removeEntity: 'remove'
    }),
    
    createAction() {
      this.editMode = false
      this.dataToBind = null
      this.rowToEdit = null
      this.dialogVisible = true
    },
    
    editAction(params) {
      this.editMode = true
      this.dataToBind = params.data
      this.rowToEdit = params.rowIndex
      this.dialogVisible = true
    },
    
    removeAction(params) {
      this.targetParams = params
      this.deleteDialogVisible = true
    },
    
    removeActionApi() {
      this.removeEntity({id: this.targetParams.data.id}).then(() => {
        notifySuccess('Role removed successfully')
        this.deleteDialogVisible = false
        
        // Force a complete refresh
        this.$nextTick(() => {
          this.loadData()
        })
      }).catch(e => {
        notify({
          type: 'error',
          title: 'Error removing role'
        })
      })
    },
    
    moduleFormDeleteAction() {
      this.removeAction({
        data: this.dataToBind,
        rowIndex: this.rowToEdit
      })
      this.dialogVisible = false
    },
    
    afterCreate(newEntity) {
      this.dialogVisible = false
      notifySuccess('Role created successfully')
      
      // Force a complete refresh
      this.$nextTick(() => {
        this.loadData()
      })
    },
    
    afterEdit(updatedData) {
      this.dialogVisible = false
      notifySuccess('Role updated successfully')
      
      // Force a complete refresh
      this.$nextTick(() => {
        this.loadData()
      })
    },
    
    userSearchUpdated(field) {
      this.userSearch = field.value
      clearTimeout(this.userSearchTimeOut)
      this.userSearchTimeOut = setTimeout(() => {
        this.currentPage = 1
        this.loadData()
      }, 500)
    },
    
    navigateToPage(page) {
      this.currentPage = page
      this.loadData()
    },
    
    getPagesCount() {
      return Math.ceil(this.recordsCounter / this.pageChunkSize)
    },
    
    refreshGrid() {
      if (this.agGrid.gridOptions && this.agGrid.gridOptions.api) {
        this.agGrid.gridOptions.api.setRowData(this.agGrid.rowData)
      }
    },
    
    loadData() {
      this.amReady = false // Show loading state
      let params = {
        page: this.currentPage
      }
      
      if (this.userSearch) {
        params.search = this.userSearch
      }
      
      this.getEntityList(params).then(r => {
        // Handle the response format from backend
        if (Array.isArray(r)) {
          // Simple array response - use the data as-is since it already has the correct structure
          this.agGrid.rowData = [...r] // Create new array to trigger reactivity
          this.recordsCounter = r.length
          this.pageCount = 1
        } else if (r.data) {
          // Paginated response
          this.agGrid.rowData = [...r.data] // Create new array to trigger reactivity
          this.recordsCounter = r.total
          this.pageCount = r.last_page
        }
        
        // Force reactivity by updating the reference
        this.$forceUpdate()
        
        this.refreshGrid()
        this.amReady = true
      }).catch(e => {
        notify({
          type: 'error',
          title: 'Error loading roles: ' + (e.message || e)
        })
        this.amReady = true
      })
    }
  },
  
  created() {
    this.currentPage = parseInt(this.$route.params.listPage) || 1
    this.loadData()
  }
}
</script>